// client/src/utils/reciboPrintLayouts.js
//
// Envolve o HTML já finalizado de um recibo emitido (recibo.htmlGerado —
// sem {{placeholders}}, o backend já substituiu no momento da emissão) no
// layout de impressão escolhido. A CSS espelha a já existente e testada em
// components/templates/recibos/TemplateReciboPreview.js
// (createA4Content/createThermalContent/createPrintContent), que reprocessa
// um HTML de AMOSTRA pra preview de template — aqui o HTML já está pronto,
// não precisamos reprocessar nada, só trocar a moldura de impressão.

export const wrapAsA4 = (htmlFinal, titulo = 'Recibo') => `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <title>${titulo}</title>
        <style>
            @media print {
                @page { size: A4 portrait; margin: 20mm; }
            }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
                font-family: Arial, Helvetica, sans-serif;
                color: #222;
                line-height: 1.4;
                background: white;
                width: 170mm;
                margin: 0 auto;
            }
            .recibo-container { width: 100%; }
            /* Logo do cabeçalho — sem limite próprio, uma <img> solta no HTML
               do template ocupava a largura inteira do recibo (max-width:100%
               só evita estourar, não deixa ela do tamanho de uma logo real). */
            .recibo-container img { max-width: 40mm; max-height: 25mm; width: auto; height: auto; display: block; margin: 0 auto 10px; object-fit: contain; }
            .recibo-container table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px; }
            .recibo-container td, .recibo-container th { padding: 4px 6px; border: 1px solid #ccc; word-wrap: break-word; }
        </style>
    </head>
    <body>
        <div class="recibo-container">${htmlFinal}</div>
    </body>
    </html>
`;

// Larguras reais de bobina de impressora térmica — 58mm cobre maquininhas de
// cartão (TPA) e mini impressoras (diâmetro de bobina 30-40mm), 80mm é o
// padrão de impressora de cupom fiscal/caixa de loja. A altura NUNCA é fixa
// (bobina contínua, não folha) — abrirJanelaImpressao calcula a altura real
// do conteúdo renderizado e injeta o @page certo antes de imprimir, então
// aqui só definimos largura/fonte; nenhum @page é declarado neste HTML.
export const wrapAsThermal = (htmlFinal, titulo = 'Recibo', larguraMm = 80) => {
    const larguraConteudo = larguraMm - 4; // padding de 2mm de cada lado
    const fonteBase = larguraMm <= 58 ? 8 : 10;
    // Logo proporcional à largura da bobina — em 58mm ela não pode chegar
    // perto de ocupar a largura toda (sobra pouco espaço pro texto).
    const larguraLogo = Math.round(larguraMm * 0.4);

    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <title>${titulo}</title>
        <style>
            * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; line-height: 1.15 !important; }
            html, body {
                width: ${larguraMm}mm !important;
                margin: 0 auto !important;
                padding: 2mm !important;
                font-family: 'Courier New', Courier, monospace !important;
                font-size: ${fonteBase}px !important;
                color: #000000 !important;
                background: white !important;
            }
            .recibo-container { width: ${larguraConteudo}mm !important; margin: 0 auto !important; }
            .recibo-container img { max-width: ${larguraLogo}mm !important; max-height: ${larguraLogo}mm !important; width: auto !important; height: auto !important; display: block !important; margin: 0 auto 2mm !important; object-fit: contain !important; }
            .recibo-container * {
                margin: 0.5px 0 !important;
                font-size: ${fonteBase}px !important;
                line-height: 1.15 !important;
                word-wrap: break-word !important;
                overflow-wrap: break-word !important;
                max-width: ${larguraConteudo}mm !important;
                font-family: 'Courier New', Courier, monospace !important;
            }
            .recibo-container h1, .recibo-container h2, .recibo-container h3 {
                font-size: ${fonteBase + 2}px !important;
                font-weight: bold !important;
                text-align: center !important;
                text-transform: uppercase !important;
            }
            .recibo-container table { width: 100% !important; border-collapse: collapse !important; table-layout: fixed !important; }
            .recibo-container td, .recibo-container th { padding: 2px 3px !important; border: 1px solid #000 !important; }
            .recibo-container hr { border: none !important; border-top: 1px dashed #ccc !important; margin: 4px 0 !important; }
        </style>
    </head>
    <body>
        <div class="recibo-container">${htmlFinal}</div>
    </body>
    </html>
`;
};

export const wrapRecibo = (htmlFinal, layout, titulo, larguraMm) =>
    layout === 'termica' ? wrapAsThermal(htmlFinal, titulo, larguraMm) : wrapAsA4(htmlFinal, titulo);

const mmParaPx = (mm) => (mm * 96) / 25.4;
const pxParaMm = (px) => (px * 25.4) / 96;

// Mesmo padrão de janela de impressão já usado em
// TemplateReciboPreview.js::handlePrint e
// AgendamentoActionsModal.js::handleGerarRecibo — abre uma janela nova,
// escreve o HTML e chama print(). Retorna false se o pop-up foi bloqueado
// (quem chama decide o alerta, pra não acoplar useAlert aqui).
//
// Pra térmica (larguraMm !== null), NÃO basta declarar @page{size} no HTML
// estático — "size: 80mm auto" não é uma sintaxe CSS válida (o valor "auto"
// não combina com uma largura em mm nessa propriedade), então o navegador
// descarta a regra inteira e cai no tamanho de página padrão (A4), que era
// exatamente o bug reportado ("modo térmico não muda, continua em A4").
// Em vez disso, esperamos o conteúdo carregar, medimos a altura real
// renderizada e injetamos um @page com largura E altura explícitas em mm —
// sintaxe válida e reconhecida pelo diálogo de impressão do Chrome como
// tamanho de página customizado.
export const abrirJanelaImpressao = (htmlWrapped, larguraMm = null) => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return false;

    printWindow.document.write(htmlWrapped);
    printWindow.document.close();

    const dispararImpressao = () => {
        if (larguraMm) {
            const alturaConteudoPx = printWindow.document.body.scrollHeight;
            const alturaMm = Math.max(pxParaMm(alturaConteudoPx) + 8, 40);
            const style = printWindow.document.createElement('style');
            style.textContent = `@page { size: ${larguraMm}mm ${alturaMm.toFixed(1)}mm; margin: 0; }`;
            printWindow.document.head.appendChild(style);
        }

        printWindow.focus();
        printWindow.print();
    };

    // scrollHeight só é confiável depois que a janela terminou de carregar
    // (imagens incluídas, ex.: a logo) — sem isso a altura térmica calculada
    // vem sempre menor que o conteúdo real.
    if (printWindow.document.readyState === 'complete') {
        setTimeout(dispararImpressao, 150);
    } else {
        printWindow.addEventListener('load', () => setTimeout(dispararImpressao, 150));
    }

    return true;
};

export const LARGURAS_TERMICA = [
    { valor: 58, label: '58mm (TPA / maquininha)' },
    { valor: 80, label: '80mm (cupom fiscal)' },
];

export { mmParaPx };
